
import { computed, defineComponent, ref } from "vue";
import { ObserveVisibility } from "vue-observe-visibility";
import { getModule } from "vuex-module-decorators";
import CiButton from "@components/base/button.vue";
import { ViewportStoreModule } from "@stores/viewport.store";

const ViewportStore = getModule(ViewportStoreModule);

export default defineComponent({
  name: "ScrollSnapSlider",
  components: {
    "ci-button": CiButton,
  },
  directives: {
    "observe-visibility": ObserveVisibility,
  },
  props: {
    items: {
      type: Array,
      require: true,
      default: () => [],
    },
    optionalItem: {
      type: Boolean,
      default: false,
    },
    displayIndicators: {
      type: Boolean,
      default: true,
    },
  },
  emits: ["slideEndReached"],

  setup(props, { emit }) {
    const scrollSnapSlider = ref<HTMLElement | null>(null);
    const currentSlide = ref<number | undefined>(0);
    const prevVisible = ref<boolean>(false);
    const nextVisible = ref<boolean>(true);

    const desktopMode = computed(() => !ViewportStore.handheld);
    const mobileMode = computed(() => ViewportStore.smartphone);
    const itemsLength = computed(() => props.items.length + (props.optionalItem ? 1 : 0));
    const handleCssClassForItemWidth = computed(() => {
      if (itemsLength.value > 1 && mobileMode.value) {
        return "sw-90";
      } else if (itemsLength.value > 2 && !desktopMode.value) {
        return "sw-48";
      } else if (itemsLength.value > 3 && desktopMode.value) {
        return "sw-30";
      } else {
        return "";
      }
    });
    const handleNavigationOnViewPort = computed(() => {
      if (mobileMode.value) {
        return false;
      } else if (!desktopMode.value && itemsLength.value > 2) {
        return true;
      } else return desktopMode.value && itemsLength.value > 3;
    });
    const prevNextButtonOnVisibilityPosition = computed(() => {
      if (!prevVisible.value) {
        return "justify-content-end";
      } else if (!nextVisible.value) {
        return "justify-content-start";
      } else {
        return "justify-content-between";
      }
    });
    const slider = computed(() => scrollSnapSlider.value as HTMLElement);

    const itemObserver = (index: number) => ({
      callback: (isVisible: boolean, entry: any) => slideVisible(isVisible, entry, index),
      throttle: 100,
      intersection: {
        root: scrollSnapSlider.value as HTMLElement,
        rootMargin: "0px",
        threshold: 0.75,
      },
    });

    const scrollSlider = (value: number) => {
      if (mobileMode.value) {
        const iterator = value >= itemsLength.value ? 0 : value;
        const firstElement = slider.value.firstChild;
        const childWidth =
          firstElement && firstElement.nodeName !== "#comment"
            ? (firstElement as HTMLElement).clientWidth
            : slider.value.clientWidth;
        slider.value.scrollLeft = childWidth * iterator;
      } else {
        slider.value.scrollBy((slider.value.clientWidth / itemsLength.value) * value, 0);
      }
    };

    const slideVisible = (isVisible: boolean, entry: any, index: number) => {
      if (!mobileMode.value) {
        if (entry.isIntersecting && entry.target === slider.value.firstChild) {
          prevVisible.value = false;
        } else if (!entry.isIntersecting && entry.target === slider.value.firstChild) {
          prevVisible.value = true;
        }
        if (entry.isIntersecting && entry.target === lastVisibleSliderItem()) {
          nextVisible.value = false;
          emit("slideEndReached");
        } else if (!entry.isIntersecting && entry.target === lastVisibleSliderItem()) {
          nextVisible.value = true;
        }
      }
      if (entry.isIntersecting && isVisible && currentSlide.value !== index) currentSlide.value = index;
    };

    const lastVisibleSliderItem = () => {
      if (slider.value.lastChild?.nodeName !== "#comment") return slider.value.lastChild;

      if (slider.value.childNodes.length < 2) return slider.value.firstChild;

      return slider.value.childNodes[slider.value.childNodes.length - 2];
    };

    const activeIndicator = (index: any) => currentSlide.value === index;

    return {
      itemObserver,
      activeIndicator,
      scrollSlider,
      itemsLength,
      scrollSnapSlider,
      mobileMode,
      desktopMode,
      currentSlide,
      handleCssClassForItemWidth,
      handleNavigationOnViewPort,
      prevNextButtonOnVisibilityPosition,
      prevVisible,
      nextVisible,
      slider,
    };
  },
});
